import { Skeleton } from '@lego/connect-components-react';
import { useTranslation } from 'react-i18next';
import { NotFoundPage } from '@/pages/not-found';
import { useUserStore } from '@/stores/user-store';
import { AppTree } from './app-tree';
import { ConfirmDialog } from './confirm-dialog';
import { GroupDetailPanel } from './group-detail-panel';
import { useOperatorAccess } from './hooks';
import { UnrestrictedGroupPanel } from './unrestricted-group-panel';

export const OperatorAccessPage = () => {
  const { t } = useTranslation();
  const { canManageOperatorAccess } = useUserStore();
  const access = useOperatorAccess();

  if (!canManageOperatorAccess()) return <NotFoundPage />;
  if (access.assignmentsLoading && !access.assignmentsLoaded) {
    return (
      <div
        className="px-400 py-400"
        role="status"
        aria-label={t('formPrintPage.loading', 'Loading...')}
      >
        <div className="mb-300 flex flex-col gap-100">
          <Skeleton width="15rem" height="2rem" showAnimation />
          <Skeleton width="24rem" height="1rem" showAnimation />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-300 lg:h-[calc(100vh-220px)] lg:min-h-[480px]">
          <div className="lg:col-span-4 mb-300 lg:mb-0 flex flex-col gap-200 rounded-lg border border-core-slate-100 bg-white p-200">
            <Skeleton width="50%" height="1rem" showAnimation />
            <Skeleton rows={6} showAnimation />
          </div>
          <div className="lg:col-span-8 min-h-[100px] rounded-lg border border-core-slate-100 bg-white p-300">
            <Skeleton width="60%" height="1.25rem" showAnimation />
          </div>
        </div>
      </div>
    );
  }

  const { selection, selectedApplication } = access;

  return (
    <>
      <div className="px-400 py-400">
        <div className="mb-300">
          <h1 className="text-heading-md font-brand font-bold text-core-slate-1300">
            {t('operatorAccessPage.display.title')}
          </h1>
          <p className="text-body-xs text-core-slate-900 mt-50">
            {t('operatorAccessPage.display.subtitle')}
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-300 lg:h-[calc(100vh-220px)] lg:min-h-[480px]">
          <AppTree
            applications={access.applicationAccess}
            selection={selection}
            assignmentsLoaded={access.assignmentsLoaded}
            onSelectGroup={access.selectGroup}
            onSelectUnrestricted={access.selectUnrestricted}
            onCommitNewGroup={access.commitNewGroup}
          />
          <section className="lg:col-span-8 border border-core-slate-100 rounded-lg overflow-hidden flex flex-col bg-white min-h-[100px]">
            {!selection ? (
              <div className="flex-1 flex items-center justify-center text-core-slate-900 text-body-sm px-400 text-center">
                <span className="lg:hidden">
                  {t('operatorAccessPage.display.selectGroupHintTop')}
                </span>
                <span className="hidden lg:inline">
                  {t('operatorAccessPage.display.selectGroupHint')}
                </span>
              </div>
            ) : selection.kind === 'unrestricted' ? (
              <UnrestrictedGroupPanel
                key={selection.application}
                application={selection.application}
                unrestricted={selectedApplication?.unrestricted ?? []}
                groupSuggestions={Array.from(selectedApplication?.groups.keys() ?? []).sort()}
                isBusy={access.isBusy}
                onRestrictOperator={access.restrictOperator}
              />
            ) : (
              <GroupDetailPanel
                key={`${selection.application}:${selection.groupName}`}
                selection={selection}
                members={access.selectedMembers}
                assignableOperators={access.assignableOperators}
                isBusy={access.isBusy}
                onRenameGroup={access.renameGroup}
                onDeleteGroup={access.requestDeleteGroup}
                onRemoveMember={access.requestRemoveMember}
                onAssignKnownOperator={access.assignKnownOperator}
                onInviteAndAssign={access.inviteAndAssign}
              />
            )}
          </section>
        </div>
      </div>
      <ConfirmDialog
        dialog={access.confirmDialog}
        isBusy={access.isBusy}
        triggerRef={access.confirmTriggerRef}
        onClose={access.closeConfirmDialog}
        onRemoveMember={access.confirmRemoveMember}
        onDeleteGroup={access.confirmDeleteGroup}
      />
    </>
  );
};
